import React from 'react';

const CHART_COLORS = ['#1976d2', '#e91e63', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#f44336', '#8bc34a', '#ff5722', '#607d8b'];

const DataChart = ({ data }) => {
  if (!data) return null;

  const {
    chartType = 'bar',
    chartTitle = '',
    chartData = [],
    xLabel = '',
    yLabel = '',
    showValues = true
  } = data;

  if (chartData.length === 0) return null;

  const svgW = 320, svgH = 280;
  const pad = { top: 30, right: 20, bottom: 50, left: 50 };
  const plotW = svgW - pad.left - pad.right;
  const plotH = svgH - pad.top - pad.bottom;

  switch (chartType) {
    case 'bar': {
      const maxVal = Math.max(...chartData.map(d => d.value), 1);
      const ceil = Math.ceil(maxVal / 5) * 5 || 10;
      const barW = Math.min(plotW / chartData.length * 0.7, 40);
      const gap = (plotW - barW * chartData.length) / (chartData.length + 1);

      // Y-axis ticks
      const ticks = 5;
      const yTicks = Array.from({ length: ticks + 1 }, (_, i) => (ceil / ticks) * i);

      return (
        <div className="diagram-container">
          {chartTitle && <div className="diagram-title">{chartTitle}</div>}
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="chart-svg" role="img" aria-label={chartTitle || 'Bar chart'}>
            {/* Y axis */}
            <line x1={pad.left} y1={pad.top} x2={pad.left} y2={pad.top + plotH} stroke="#333" strokeWidth="1.5" />
            {/* X axis */}
            <line x1={pad.left} y1={pad.top + plotH} x2={pad.left + plotW} y2={pad.top + plotH} stroke="#333" strokeWidth="1.5" />
            {/* Grid lines and Y labels */}
            {yTicks.map((v, i) => {
              const y = pad.top + plotH - (v / ceil) * plotH;
              return (
                <g key={`yt${i}`}>
                  <line x1={pad.left} y1={y} x2={pad.left + plotW} y2={y} stroke="#e0e0e0" strokeWidth="0.5" />
                  <text x={pad.left - 6} y={y + 4} textAnchor="end" className="diagram-grid-label">{v}</text>
                </g>
              );
            })}
            {/* Bars */}
            {chartData.map((d, i) => {
              const x = pad.left + gap + i * (barW + gap);
              const barH = (d.value / ceil) * plotH;
              const y = pad.top + plotH - barH;
              const color = d.color || CHART_COLORS[i % CHART_COLORS.length];
              return (
                <g key={`bar${i}`}>
                  <rect x={x} y={y} width={barW} height={barH} fill={color} rx="2" opacity="0.85" />
                  {showValues && (
                    <text x={x + barW / 2} y={y - 5} textAnchor="middle" className="diagram-bar-value">{d.value}</text>
                  )}
                  <text x={x + barW / 2} y={pad.top + plotH + 14}
                    textAnchor="middle" className="diagram-bar-label"
                    transform={chartData.length > 6 ? `rotate(-35, ${x + barW / 2}, ${pad.top + plotH + 14})` : ''}>
                    {d.label}
                  </text>
                </g>
              );
            })}
            {/* Axis labels */}
            {yLabel && <text x={14} y={pad.top + plotH / 2} textAnchor="middle"
              transform={`rotate(-90, 14, ${pad.top + plotH / 2})`} className="diagram-axis-title">{yLabel}</text>}
            {xLabel && <text x={pad.left + plotW / 2} y={svgH - 5} textAnchor="middle" className="diagram-axis-title">{xLabel}</text>}
          </svg>
        </div>
      );
    }

    case 'pie': {
      const total = chartData.reduce((s, d) => s + d.value, 0) || 1;
      const cx = svgW / 2, cy = svgH / 2 - 5;
      const r = Math.min(plotW, plotH) / 2 - 10;
      let cumAngle = -Math.PI / 2;

      const slices = chartData.map((d, i) => {
        const angle = (d.value / total) * 2 * Math.PI;
        const startAngle = cumAngle;
        cumAngle += angle;
        const endAngle = cumAngle;
        const largeArc = angle > Math.PI ? 1 : 0;
        const x1 = cx + r * Math.cos(startAngle);
        const y1 = cy + r * Math.sin(startAngle);
        const x2 = cx + r * Math.cos(endAngle);
        const y2 = cy + r * Math.sin(endAngle);
        const midAngle = startAngle + angle / 2;
        const labelR = r * 0.65;
        const lx = cx + labelR * Math.cos(midAngle);
        const ly = cy + labelR * Math.sin(midAngle);
        const color = d.color || CHART_COLORS[i % CHART_COLORS.length];
        const pct = Math.round(d.value / total * 100);
        const path = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
        return { path, color, lx, ly, label: d.label, pct, i };
      });

      return (
        <div className="diagram-container">
          {chartTitle && <div className="diagram-title">{chartTitle}</div>}
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="chart-svg" role="img" aria-label={chartTitle || 'Pie chart'}>
            {slices.map(s => (
              <g key={`pie${s.i}`}>
                <path d={s.path} fill={s.color} stroke="#fff" strokeWidth="2" opacity="0.85" />
                {s.pct >= 5 && (
                  <text x={s.lx} y={s.ly} textAnchor="middle" dominantBaseline="middle"
                    className="diagram-pie-label">{s.pct}%</text>
                )}
              </g>
            ))}
            {/* Legend */}
            {chartData.map((d, i) => (
              <g key={`leg${i}`} transform={`translate(${pad.left}, ${svgH - 16})`}>
                <rect x={i * (plotW / chartData.length)} y={0} width="10" height="10"
                  fill={d.color || CHART_COLORS[i % CHART_COLORS.length]} rx="2" />
                <text x={i * (plotW / chartData.length) + 14} y={9}
                  className="diagram-legend-label">{d.label}</text>
              </g>
            ))}
          </svg>
        </div>
      );
    }

    case 'histogram': {
      const maxVal = Math.max(...chartData.map(d => d.value), 1);
      const ceil = Math.ceil(maxVal / 5) * 5 || 10;
      const barW = plotW / chartData.length;
      const ticks = 5;
      const yTicks = Array.from({ length: ticks + 1 }, (_, i) => (ceil / ticks) * i);

      return (
        <div className="diagram-container">
          {chartTitle && <div className="diagram-title">{chartTitle}</div>}
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="chart-svg" role="img" aria-label={chartTitle || 'Histogram'}>
            <line x1={pad.left} y1={pad.top} x2={pad.left} y2={pad.top + plotH} stroke="#333" strokeWidth="1.5" />
            <line x1={pad.left} y1={pad.top + plotH} x2={pad.left + plotW} y2={pad.top + plotH} stroke="#333" strokeWidth="1.5" />
            {yTicks.map((v, i) => {
              const y = pad.top + plotH - (v / ceil) * plotH;
              return (
                <g key={`yt${i}`}>
                  <line x1={pad.left} y1={y} x2={pad.left + plotW} y2={y} stroke="#e0e0e0" strokeWidth="0.5" />
                  <text x={pad.left - 6} y={y + 4} textAnchor="end" className="diagram-grid-label">{v}</text>
                </g>
              );
            })}
            {chartData.map((d, i) => {
              const x = pad.left + i * barW;
              const barH = (d.value / ceil) * plotH;
              const y = pad.top + plotH - barH;
              const color = d.color || CHART_COLORS[0];
              return (
                <g key={`hbar${i}`}>
                  <rect x={x} y={y} width={barW} height={barH} fill={color} stroke="#fff" strokeWidth="1" opacity="0.8" />
                  {showValues && <text x={x + barW / 2} y={y - 5} textAnchor="middle" className="diagram-bar-value">{d.value}</text>}
                  <text x={x + barW / 2} y={pad.top + plotH + 14} textAnchor="middle" className="diagram-bar-label">{d.label}</text>
                </g>
              );
            })}
            {yLabel && <text x={14} y={pad.top + plotH / 2} textAnchor="middle"
              transform={`rotate(-90, 14, ${pad.top + plotH / 2})`} className="diagram-axis-title">{yLabel}</text>}
            {xLabel && <text x={pad.left + plotW / 2} y={svgH - 5} textAnchor="middle" className="diagram-axis-title">{xLabel}</text>}
          </svg>
        </div>
      );
    }

    case 'line': {
      const maxVal = Math.max(...chartData.map(d => d.value), 1);
      const ceil = Math.ceil(maxVal / 5) * 5 || 10;
      const ticks = 5;
      const yTicks = Array.from({ length: ticks + 1 }, (_, i) => (ceil / ticks) * i);
      const step = chartData.length > 1 ? plotW / (chartData.length - 1) : plotW;

      const points = chartData.map((d, i) => {
        const x = pad.left + (chartData.length > 1 ? i * step : step / 2);
        const y = pad.top + plotH - (d.value / ceil) * plotH;
        return [x, y];
      });
      const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ');

      return (
        <div className="diagram-container">
          {chartTitle && <div className="diagram-title">{chartTitle}</div>}
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="chart-svg" role="img" aria-label={chartTitle || 'Line chart'}>
            <line x1={pad.left} y1={pad.top} x2={pad.left} y2={pad.top + plotH} stroke="#333" strokeWidth="1.5" />
            <line x1={pad.left} y1={pad.top + plotH} x2={pad.left + plotW} y2={pad.top + plotH} stroke="#333" strokeWidth="1.5" />
            {yTicks.map((v, i) => {
              const y = pad.top + plotH - (v / ceil) * plotH;
              return (
                <g key={`yt${i}`}>
                  <line x1={pad.left} y1={y} x2={pad.left + plotW} y2={y} stroke="#e0e0e0" strokeWidth="0.5" />
                  <text x={pad.left - 6} y={y + 4} textAnchor="end" className="diagram-grid-label">{v}</text>
                </g>
              );
            })}
            <path d={pathD} fill="none" stroke="#1976d2" strokeWidth="2.5" />
            {points.map((p, i) => (
              <g key={`lp${i}`}>
                <circle cx={p[0]} cy={p[1]} r="4" fill="#1976d2" />
                {showValues && <text x={p[0]} y={p[1] - 10} textAnchor="middle" className="diagram-bar-value">{chartData[i].value}</text>}
                <text x={p[0]} y={pad.top + plotH + 14} textAnchor="middle" className="diagram-bar-label">{chartData[i].label}</text>
              </g>
            ))}
            {yLabel && <text x={14} y={pad.top + plotH / 2} textAnchor="middle"
              transform={`rotate(-90, 14, ${pad.top + plotH / 2})`} className="diagram-axis-title">{yLabel}</text>}
            {xLabel && <text x={pad.left + plotW / 2} y={svgH - 5} textAnchor="middle" className="diagram-axis-title">{xLabel}</text>}
          </svg>
        </div>
      );
    }

    default:
      return <div className="diagram-container"><p>Unknown chart type: {chartType}</p></div>;
  }
};

export default DataChart;
